import React, { useEffect, useMemo, useRef, useState } from "react";
import { createChart, CandlestickSeries, HistogramSeries, LineSeries } from "lightweight-charts";
import { BarChart3, Loader2, Search, Send, ArrowUpDown } from "lucide-react";
import { openbbGet, nexusCommand, nexusChannel } from "../lib/api";
import { utcTickFormatter } from "../lib/tz";

const TABS = ["Overview", "Financials", "Technical Analysis", "Comparison", "Ownership", "Company"];
const RANGES = [["1M", 30], ["3M", 90], ["6M", 180], ["1Y", 365], ["5Y", 1825]];
const fmt = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v !== "number") return String(v);
  const a = Math.abs(v);
  if (a >= 1e12) return `${(v / 1e12).toFixed(2)}T`;
  if (a >= 1e9) return `${(v / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
  return a < 10 ? v.toFixed(4).replace(/0+$/, "").replace(/\.$/, "") : v.toLocaleString(undefined, { maximumFractionDigits: 2 });
};
const tone = (v) => (typeof v === "number" ? (v > 0 ? "text-emerald-400" : v < 0 ? "text-rose-400" : "text-cyan-100") : "text-cyan-100/80");

const CandleChart = ({ candles, showSma }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current || !candles?.length) return undefined;
    const chart = createChart(ref.current, {
      height: 320, layout: { background: { color: "#060b18" }, textColor: "#9fe8f0" },
      grid: { vertLines: { color: "#0d1a2e" }, horzLines: { color: "#0d1a2e" } }, rightPriceScale: { borderColor: "#123" },
      timeScale: { borderColor: "#123", tickMarkFormatter: utcTickFormatter }, autoSize: true, localization: { locale: "en-US" },
    });
    chart.addSeries(CandlestickSeries, { upColor: "#34d399", downColor: "#fb7185", borderVisible: false, wickUpColor: "#34d399", wickDownColor: "#fb7185" })
      .setData(candles.map((c) => ({ time: c.t, open: c.o, high: c.h, low: c.l, close: c.c })));
    const vol = chart.addSeries(HistogramSeries, { priceScaleId: "vol", priceFormat: { type: "volume" } });
    chart.priceScale("vol").applyOptions({ scaleMargins: { top: 0.8, bottom: 0 } });
    vol.setData(candles.map((c) => ({ time: c.t, value: c.v, color: c.c >= c.o ? "#34d39955" : "#fb718555" })));
    if (showSma) {
      [["sma20", "#00F0FF"], ["sma50", "#facc15"]].forEach(([k, color]) =>
        chart.addSeries(LineSeries, { color, lineWidth: 1 }).setData(candles.filter((c) => c[k] != null).map((c) => ({ time: c.t, value: c[k] }))));
    }
    chart.timeScale().fitContent();
    return () => chart.remove();
  }, [candles, showSma]);
  return <div ref={ref} className="w-full" data-testid="openbb-chart" />;
};

const SortTable = ({ rows, cols, testid }) => {
  const [sort, setSort] = useState({ k: null, dir: -1 });
  const data = useMemo(() => {
    if (!sort.k) return rows;
    return [...rows].sort((a, b) => {
      const x = a[sort.k], y = b[sort.k];
      if (typeof x === "number" && typeof y === "number") return (x - y) * sort.dir;
      return String(x ?? "").localeCompare(String(y ?? "")) * sort.dir;
    });
  }, [rows, sort]);
  if (!rows?.length) return <p className="text-xs text-cyan-200/40">No data.</p>;
  return (
    <div className="overflow-x-auto" data-testid={testid}>
      <table className="w-full text-xs">
        <thead><tr>{cols.map((c) => (
          <th key={c} onClick={() => setSort((s) => ({ k: c, dir: s.k === c ? -s.dir : -1 }))} data-testid={`${testid}-sort-${c}`}
            className="text-left px-2 py-1.5 text-cyan-200/60 font-semibold cursor-pointer whitespace-nowrap hover:text-[#00F0FF]">
            <span className="inline-flex items-center gap-1">{c}<ArrowUpDown className="w-3 h-3 opacity-40" /></span></th>))}</tr></thead>
        <tbody>{data.map((r, i) => (
          <tr key={i} className="border-t border-[#00F0FF10]">{cols.map((c) => (
            <td key={c} className={`px-2 py-1.5 whitespace-nowrap ${tone(r[c])}`}>{fmt(r[c])}</td>))}</tr>))}</tbody>
      </table>
    </div>
  );
};

const Fin = ({ d }) => {
  const [st, setSt] = useState("income");
  const s = d?.[st];
  return (
    <div className="space-y-2">
      <div className="flex gap-2">{["income", "balance", "cashflow"].map((k) => (
        <button key={k} onClick={() => setSt(k)} data-testid={`openbb-fin-${k}`}
          className={`px-2.5 py-1 rounded-full text-[11px] font-bold ${st === k ? "bg-[#00F0FF] text-[#030712]" : "text-cyan-200/60 border border-[#00F0FF22]"}`}>{k}</button>))}</div>
      {s && <SortTable rows={s.rows} cols={["item", ...s.periods]} testid="openbb-fin-table" />}
    </div>
  );
};

const CompanyView = ({ d }) => (
  <div className="space-y-3 text-xs text-cyan-100/80" data-testid="openbb-company">
    <p className="text-sm text-cyan-50 font-bold">{d.profile?.longName} <span className="text-cyan-200/50 font-normal">· {d.profile?.sector} · {d.profile?.industry}</span></p>
    <p className="leading-relaxed">{(d.profile?.longBusinessSummary || "").slice(0, 700)}</p>
    <p className="text-cyan-200/60 font-semibold">Earnings history</p>
    <SortTable rows={d.earnings || []} cols={["quarter", "epsActual", "epsEstimate", "epsDifference", "surprisePercent"]} testid="openbb-earnings" />
    <p className="text-cyan-200/60 font-semibold">Dividends</p>
    <SortTable rows={d.dividends || []} cols={["date", "amount"]} testid="openbb-dividends" />
    <p className="text-cyan-200/60 font-semibold">Latest SEC filings</p>
    <div className="space-y-1">{(d.filings || []).map((f, i) => (
      <a key={i} href={f.url} target="_blank" rel="noreferrer" className="block hover:text-[#00F0FF]" data-testid={`openbb-filing-${i}`}>{f.date} · {f.form}{f.error ? f.error : ""}</a>))}</div>
  </div>
);

const Copilot = ({ symbol, userId }) => {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [ans, setAns] = useState("");
  const ask = async () => {
    if (!q.trim() || busy) return;
    setBusy(true); setAns("");
    try {
      let r = await nexusCommand(userId, `[OpenBB Research context: ticker ${symbol}] ${q.trim()}`, "chat");
      for (let i = 0; r.pending && i < 100; i++) {
        await new Promise((ok) => setTimeout(ok, 2000));
        const ch = await nexusChannel(userId);
        if ((ch.turn_id || 0) > (r.prev_turn_id || 0) && ch.reply_text) r = ch;
        else if (ch.error && ch.status !== "thinking") throw new Error(ch.error);
      }
      setAns(r.reply_text || "(no answer)");
    }
    catch (e) { setAns(`Copilot failed: ${e?.response?.data?.detail || e?.message || "error"}`); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()} placeholder={`Ask Copilot about ${symbol}…`}
          data-testid="openbb-copilot-input" className="flex-1 bg-[#030712] border border-[#00F0FF22] rounded-lg px-3 py-2 text-sm text-cyan-50 outline-none focus:border-[#00F0FF66]" />
        <button onClick={ask} disabled={busy} data-testid="openbb-copilot-send" className="px-3 rounded-lg bg-[#00F0FF] text-[#030712] font-bold disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</button>
      </div>
      {ans && <p className="text-sm text-cyan-100/90 whitespace-pre-wrap bg-[#030712] rounded-lg p-3 border border-[#00F0FF14]" data-testid="openbb-copilot-answer">{ans}</p>}
    </div>
  );
};

export const OpenBBResearch = ({ userId }) => {
  const [input, setInput] = useState("AAPL");
  const [symbol, setSymbol] = useState("AAPL");
  const [cmpInput, setCmpInput] = useState("AAPL,MSFT,NVDA");
  const [tab, setTab] = useState("Overview");
  const [days, setDays] = useState(180);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    const kind = { Overview: "overview", Financials: "financials", "Technical Analysis": "technicals", Comparison: "compare", Ownership: "ownership", Company: "company" }[tab];
    const params = kind === "compare" ? { symbols: cmpInput } : { symbol, ...(kind === "overview" || kind === "technicals" ? { days } : {}) };
    let live = true;
    setLoading(true); setErr(""); setData(null);
    openbbGet(kind, params).then((d) => { if (live) { d.error ? setErr(d.error) : setData(d); } })
      .catch((e) => live && setErr(e?.response?.data?.detail || "Load failed")).finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [tab, symbol, days, cmpInput]);

  const q = data?.quote;
  return (
    <div className="bg-[#060b18] border border-[#00F0FF22] rounded-2xl p-4 space-y-3" data-testid="openbb-research-card">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-[#00F0FF] font-bold flex items-center gap-2 mr-auto"><BarChart3 className="w-4 h-4" /> OpenBB Research</p>
        <form onSubmit={(e) => { e.preventDefault(); setSymbol(input.trim().toUpperCase() || "AAPL"); }} className="flex gap-1">
          <input value={input} onChange={(e) => setInput(e.target.value)} data-testid="openbb-ticker-input"
            className="w-24 bg-[#030712] border border-[#00F0FF22] rounded-lg px-2 py-1 text-sm text-cyan-50 uppercase outline-none focus:border-[#00F0FF66]" />
          <button type="submit" data-testid="openbb-ticker-go" className="px-2 rounded-lg border border-[#00F0FF44] text-[#00F0FF]"><Search className="w-4 h-4" /></button>
        </form>
      </div>
      <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="openbb-tabs">{TABS.map((t) => (
        <button key={t} onClick={() => setTab(t)} data-testid={`openbb-tab-${t.toLowerCase().replace(/\s+/g, "-")}`}
          className={`px-3 py-1 rounded-full text-[11px] font-bold whitespace-nowrap transition-colors ${tab === t ? "bg-[#00F0FF] text-[#030712]" : "text-cyan-200/60 hover:text-cyan-100 border border-[#00F0FF22]"}`}>{t}</button>))}</div>
      {(tab === "Overview" || tab === "Technical Analysis") && (
        <div className="flex gap-1" data-testid="openbb-range">{RANGES.map(([l, d]) => (
          <button key={l} onClick={() => setDays(d)} data-testid={`openbb-range-${l}`}
            className={`px-2 py-0.5 rounded text-[10px] font-bold ${days === d ? "text-[#030712] bg-cyan-300" : "text-cyan-200/50"}`}>{l}</button>))}</div>)}
      {tab === "Comparison" && (
        <input value={cmpInput} onChange={(e) => setCmpInput(e.target.value.toUpperCase())} data-testid="openbb-compare-input" placeholder="AAPL,MSFT,NVDA"
          className="w-full bg-[#030712] border border-[#00F0FF22] rounded-lg px-3 py-1.5 text-sm text-cyan-50 outline-none" />)}
      <div className="min-h-[120px]" data-testid="openbb-content">
        {loading && <p className="text-xs text-cyan-200/50 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading {symbol}…</p>}
        {err && <p className="text-xs text-rose-300" data-testid="openbb-error">{err}</p>}
        {data?.quote && tab === "Overview" && (
          <div className="space-y-2">
            {q && <p className="text-sm" data-testid="openbb-quote"><span className="text-cyan-50 font-bold text-lg">{data.symbol} ${fmt(q.price)}</span>{" "}
              <span className={tone(q.change)}>{q.change >= 0 ? "+" : ""}{fmt(q.change)} ({fmt(q.change_pct)}%)</span>
              <span className="text-cyan-200/50 text-xs"> · 52w {fmt(q.year_low)}–{fmt(q.year_high)} · cap {fmt(q.market_cap)}</span></p>}
            <CandleChart candles={data.candles} />
          </div>)}
        {data?.latest && tab === "Technical Analysis" && (
          <div className="space-y-2">
            <p className="text-xs text-cyan-100/80" data-testid="openbb-tech-latest">Close {fmt(data.latest.close)} · <span className="text-[#00F0FF]">SMA20 {fmt(data.latest.sma20)}</span> · <span className="text-yellow-300">SMA50 {fmt(data.latest.sma50)}</span> · RSI14 {fmt(data.latest.rsi14)} · trend <span className={data.latest.trend === "up" ? "text-emerald-400" : "text-rose-400"}>{data.latest.trend}</span></p>
            <CandleChart candles={data.candles} showSma />
          </div>)}
        {data?.income && tab === "Financials" && <Fin d={data} />}
        {data?.rows && tab === "Comparison" && <SortTable rows={data.rows} cols={["symbol", "price", "market_cap", "pe", "forward_pe", "ps", "ev_ebitda", "profit_margin", "revenue_growth"]} testid="openbb-compare-table" />}
        {data?.institutions && tab === "Ownership" && (
          <div className="space-y-2">
            <p className="text-xs text-cyan-100/80" data-testid="openbb-major">{Object.entries(data.major || {}).map(([k, v]) => `${k}: ${fmt(v)}`).join(" · ")}</p>
            <SortTable rows={data.institutions} cols={["Holder", "pctHeld", "Shares", "Value", "pctChange", "Date Reported"]} testid="openbb-institutions" />
          </div>)}
        {data?.profile && tab === "Company" && <CompanyView d={data} />}
      </div>
      <Copilot symbol={symbol} userId={userId} />
    </div>
  );
};
